import { BadgePill, PillSize, StandardLink } from '@brandfolder/react';
import { t, Trans } from '@lingui/macro';
import React, { FunctionComponent, useState } from 'react';

import { I18nProviderWrapper } from '@components/common/I18nProviderWrapper';
import { PeopleTaggingBackfillState } from '@components/people_tags/customPeopleTagTypes';
import { SettingsEnableDisable } from '@components/settings/shared/settings-enable-disable';

interface PeopleTaggingProps {
  defaultEnabled: boolean | null;
}

function getUiState(): PeopleTaggingBackfillState {
  return BFG.brandfolderSettings?.people_tagging_ui_state
    ?? PeopleTaggingBackfillState.Ready;
}

function getStatusBadge(
  uiState: PeopleTaggingBackfillState
): JSX.Element | null {
  switch (uiState) {
    case PeopleTaggingBackfillState.Scanning:
      return (
        <BadgePill
          backgroundColor="#D0F7EF"
          fontColor="#1C806B"
          fontSize={12}
          radius={4}
          size={PillSize.XSmall}
        >
          <Trans>Scanning</Trans>
        </BadgePill>
      );
    case PeopleTaggingBackfillState.Delayed:
      return (
        <BadgePill
          backgroundColor="#FCEEB5"
          fontColor="#944A12"
          fontSize={12}
          radius={4}
          size={PillSize.XSmall}
        >
          <Trans>Scan delayed</Trans>
        </BadgePill>
      );
    case PeopleTaggingBackfillState.Scheduled:
    case PeopleTaggingBackfillState.NotStarted:
      return (
        <BadgePill
          backgroundColor="#EBECF0"
          fontColor="#4E5363"
          fontSize={12}
          radius={4}
          size={PillSize.XSmall}
        >
          <Trans>Scan scheduled</Trans>
        </BadgePill>
      );
    default:
      return null;
  }
}

const description = <Trans>Allow photos of people to be automatically tagged after a person has been identified and tagged by an Admin or Contributor.</Trans>; // eslint-disable-line max-len

const PeopleTagging: FunctionComponent<PeopleTaggingProps> = ({
  defaultEnabled
}) => {
  const initialEnabled = defaultEnabled === null ? true : defaultEnabled;
  const [uiState, setUiState] = useState(getUiState);
  const [isEnabled, setIsEnabled] = useState(initialEnabled);

  const handleUpdate = (enabled: boolean): void => {
    setIsEnabled(enabled);
    if (BFG.brandfolderSettings) {
      BFG.brandfolderSettings.people_tagging = enabled;
      if (enabled) {
        BFG.brandfolderSettings.people_tagging_ui_state = PeopleTaggingBackfillState.NotStarted;
        setUiState(PeopleTaggingBackfillState.NotStarted);
      }
    }
  };

  const badge = isEnabled ? getStatusBadge(uiState) : null;
  const slug = BFG.brandfolder_slug || '';
  const peopleHubUrl =
    `/${slug}/bulk_management#people_management`;

  // eslint-disable-next-line max-len
  const enabledBody = `<a href="${peopleHubUrl}" class="view-status-btn" style="display:inline-flex;align-items:center;justify-content:center;height:28px;padding:0 8px;border:1px solid #8F929C;border-radius:4px;font-size:12px;font-weight:500;color:#4E5363;text-decoration:none;margin-top:4px;cursor:pointer;">View Status</a>`;

  // eslint-disable-next-line max-len
  const enabledTitle = t`AI People tagging enabled. We're processing your assets now. People tags will appear once processing is complete`;

  return (
    <I18nProviderWrapper>
      <SettingsEnableDisable
        defaultEnabled={initialEnabled}
        description={description}
        errorTitle={t`Error updating AI People Tagging setting. Please contact support.`}
        id="people-tagging-setting"
        isNew={true}
        onUpdate={handleUpdate}
        showDisableInfoModal={true}
        statusBadge={badge}
        successBody={enabledBody}
        successTitle={enabledTitle}
        successTitleDisabled={t`People Tagging is now disabled.`}
        title={t`AI People Tagging`}
        updateResourceKey="peopleTagging"
      />
    </I18nProviderWrapper>
  );
};

export default PeopleTagging;
